import type { PermissionsClaims } from "@src/auth/permissions/permissionsClaims";
import { bugsnagClient } from "@src/bugsnag";
import { isDev, isProd } from "@src/env";
import {
  getUserImageUrl,
  recursiveCamelCaseCipherTypesafe,
} from "@src/global_functions/postgrestApi/common";
import { CamelCasedPropertiesDeep } from "type-fest";
import FetchClients, { ClientOutput } from "../client";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";

type CurrentUserInfo = ClientOutput["auth"]["CurrentUserInfo"];

export type CurrentUser = Omit<
  CamelCasedPropertiesDeep<CurrentUserInfo>,
  // `role` is unused, and the rest are the legacy single-document terms flow the
  // unified `termsAndConditions` contract replaces - the API still returns them
  | "portfolioManagerUserIds"
  | "permissions"
  | "role"
  | "requiresTerms"
  | "termsAccepted"
  | "termsAcceptedAt"
> & {
  // the endpoint types this as `unknown`, so it is narrowed at runtime below
  portfolioManagerUserIds: number[];
  /**
   * Populated by UserContextProvider from the ID token claims rather than from
   * this response - see getParsedTokenPermissions.
   */
  permissions?: PermissionsClaims | null;
  /** Derived from `imageKey` */
  imageURL?: string | null;
  /** Derived from `userPermissions` */
  readOnly: boolean;
};

export const CURRENT_USER_KEY = "current-user";

const toPortfolioManagerUserIds = (
  ids: CurrentUserInfo["portfolio_manager_user_ids"]
) =>
  Array.isArray(ids)
    ? ids.filter((userId): userId is number => typeof userId === "number")
    : [];

const reportCurrentUserError = (error: unknown) => {
  if (isDev) {
    console.log(error); // eslint-disable-line no-console
  } else if (isProd) {
    bugsnagClient?.notify(
      error instanceof Error ? error : new Error(String(error)),
      (event) => {
        event.severity = "warning";
        event.addMetadata("error", { type: "Current user request failed" });
      }
    );
  }
};

const toCurrentUser = (data: CurrentUserInfo): CurrentUser => ({
  id: data.id,
  email: data.email,
  accountId: data.account_id,
  firstName: data.first_name,
  lastName: data.last_name,
  phoneNumber: data.phone_number,
  superAdmin: data.super_admin,
  imageKey: data.image_key,
  invitationAcceptedAt: data.invitation_accepted_at,
  invitationSentAt: data.invitation_sent_at,
  resetPasswordSentAt: data.reset_password_sent_at,
  jobTitle: data.job_title,
  portfolioManagerUserIds: toPortfolioManagerUserIds(
    data.portfolio_manager_user_ids
  ),
  isSelfSignUp: data.is_self_sign_up,
  fromNominator: data.from_nominator,
  userPermissions: data.user_permissions,
  imageURL: getUserImageUrl(data.image_key),
  readOnly: data.user_permissions === "read_only",
  termsAndConditions: recursiveCamelCaseCipherTypesafe(
    data.terms_and_conditions
  ),
});

export const getCurrentUserInfo = async (): Promise<CurrentUser> => {
  const { data, error } = await FetchClients.auth.GET("/current_user/info");

  if (!data) {
    reportCurrentUserError(error);
    throw isBackpackResponseErrorBody(error)
      ? new BackpackServiceError(error)
      : new Error("Current user authentication failed.");
  }

  return toCurrentUser(data);
};
